import React from 'react';
import { Link } from 'react-router-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export default function TermsOfServicePage() {
  const lastUpdated = "April 9, 2026";

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="terms-of-service-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="tos-logo-link">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <h1
          className="text-3xl sm:text-4xl font-bold text-white mb-2"
          style={{ fontFamily: 'Cabinet Grotesk' }}
          data-testid="terms-title"
        >
          Terms of Service
        </h1>
        <p className="text-sm text-[#999999] mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-[#CCCCCC] text-sm leading-relaxed">
          <Section title="1. Acceptance">
            <p>
              These Terms of Service (&quot;Terms&quot;) govern your access to and use of Divispend (&quot;Service&quot;, &quot;we&quot;,
              &quot;us&quot;). By creating an account, sending or receiving money, or listing or buying goods or services
              on the Service, you agree to be bound by these Terms and our{' '}
              <Link to="/privacy_policy" className="text-[#22C55E] hover:underline">Privacy Policy</Link>.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>You must be at least 18 years old and legally capable of forming a binding contract to use Divispend. You must provide accurate and complete information during registration and keep it current.</p>
          </Section>

          <Section title="3. Account Security">
            <p>You are responsible for safeguarding your credentials and for all activity that occurs under your account. We strongly recommend enabling Two-Factor Authentication. Notify us immediately at <a href="mailto:security@divispend.com" className="text-[#22C55E] hover:underline">security@divispend.com</a> if you suspect unauthorized access.</p>
          </Section>

          <Section title="4. Money Movement">
            <p>Divispend partners with regulated payment processors (Stripe, PayPal) to facilitate transactions. We are not a bank. Funds held in your Divispend wallet are not insured by the FDIC. Withdrawal requests are processed within applicable processor timelines.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Sending money: by initiating a send, you authorize the debit of your wallet or linked payment method.</li>
              <li>Receiving money: incoming funds are credited to your wallet after the processor confirms settlement.</li>
              <li>Refunds and chargebacks are handled per the payment processor&apos;s policies. You may be liable for amounts disputed by the buyer.</li>
              <li>Fees: applicable platform and processor fees are disclosed at the time of transaction.</li>
            </ul>
          </Section>

          <Section title="5. Marketplace Listings">
            <p>Sellers are solely responsible for the accuracy of their listings, the legality of the items or services they offer, and the fulfillment of orders. Divispend reserves the right to remove listings that violate these Terms or applicable law.</p>
            <p>Prohibited listings include but are not limited to: illegal items, controlled substances, firearms, counterfeit goods, services that facilitate illegal activity, and content that infringes third-party rights.</p>
          </Section>

          <Section title="6. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use the Service for money laundering, terrorist financing, or any other illegal activity.</li>
              <li>Attempt to bypass our security controls, including authentication, rate limiting, or fraud detection.</li>
              <li>Reverse engineer, scrape, or otherwise extract data from the Service except via published APIs.</li>
              <li>Impersonate another person or misrepresent your affiliation with any person or entity.</li>
              <li>Submit false, misleading, or fraudulent transaction or dispute information.</li>
            </ul>
          </Section>

          <Section title="7. Fees & Taxes">
            <p>Platform fees, processor fees, and applicable taxes are disclosed at the time of transaction or in your account fee schedule. You are responsible for reporting and paying any taxes that arise from your use of the Service.</p>
          </Section>

          <Section title="8. Account Suspension & Termination">
            <p>We may suspend or terminate your account, with or without notice, if we believe you have violated these Terms, applicable law, or our risk policies. You may close your account at any time via your settings; we will process pending settlements and retain records as required by law.</p>
          </Section>

          <Section title="9. Disclaimers">
            <p>THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL IMPLIED WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>TO THE FULLEST EXTENT PERMITTED BY LAW, DIVISPEND&apos;S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF (a) AMOUNTS YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM AND (b) USD 100. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.</p>
          </Section>

          <Section title="11. Dispute Resolution & Governing Law">
            <p>Any dispute arising out of or relating to these Terms or the Service shall be resolved by binding arbitration administered by JAMS under its Streamlined Arbitration Rules, in the State of Delaware, USA, and judgment on the award may be entered in any court of competent jurisdiction. You and Divispend each waive any right to a jury trial and to participate in a class action.</p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>We may update these Terms from time to time. Material changes will be communicated by email or via the Service at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance.</p>
          </Section>

          <Section title="13. Contact">
            <p>Questions about these Terms? Email us at <a href="mailto:legal@divispend.com" className="text-[#22C55E] hover:underline">legal@divispend.com</a>. For security disclosures, write to <a href="mailto:security@divispend.com" className="text-[#22C55E] hover:underline">security@divispend.com</a>.</p>
          </Section>

          <div className="pt-8 border-t border-[#222222] flex items-center justify-between text-xs text-[#666666]">
            <Link to="/privacy_policy" className="hover:text-white">Privacy Policy →</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Security →</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
